'use client';

import type { IProductListItem } from '@/api/types/product';
import type { Product } from '@/components/Product/ProductCard';
import { env } from '@/utils/env';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import type { MarketingRankingProductCardProps } from './MarketingRankingProductCard';

export const MARKETING_BRAND_PRODUCTS_SECTION_ID = 'marketing-brand-products-section';

export function withS3Url(path?: string): string {
  const normalizedPath = typeof path === 'string' ? path.trim() : '';
  if (!normalizedPath) {
    return '';
  }
  if (/^(https?:)?\/\//.test(normalizedPath)) {
    return normalizedPath;
  }
  return `${env.NEXT_PUBLIC_S3_URL}${normalizedPath}`;
}

export type MarketingProductItem = IProductListItem & Record<string, unknown>;
export type MarketingFlashSaleProductItem = MarketingProductItem;

export function getStringValue(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

export function getNumberLikeString(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return getStringValue(value);
}

function getFiniteNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const numberValue = Number(value);

  return Number.isFinite(numberValue) ? numberValue : undefined;
}

export function getMarketingFlashSaleProductImage(product: MarketingFlashSaleProductItem) {
  const image = product.image;

  if (Array.isArray(image?.product) && image.product[0]) {
    return withS3Url(image.product[0]);
  }

  if (typeof image === 'string') {
    return withS3Url(image);
  }

  return (
    withS3Url(getStringValue(product.img_url)) ||
    withS3Url(getStringValue(product.main_image)) ||
    withS3Url(getStringValue(product.product_image)) ||
    withS3Url(getStringValue(product.image_url))
  );
}

export function getMarketingProductCardProduct(
  product: MarketingProductItem,
  image: string,
): Product {
  const sourceImage = product.image;
  const hasProductImage =
    typeof sourceImage === 'object' &&
    sourceImage !== null &&
    'product' in sourceImage &&
    Array.isArray(sourceImage.product) &&
    sourceImage.product.length > 0;
  let productImage: unknown = { product: [] };
  if (image && typeof sourceImage === 'object' && sourceImage !== null) {
    productImage = { ...sourceImage, product: [image], product_gray: [] };
  } else if (image) {
    productImage = { product: [image], product_gray: [] };
  } else if (hasProductImage) {
    productImage = sourceImage;
  }
  const price =
    getNumberLikeString(product.price) || getNumberLikeString(product.original_price) || '79.99';
  const sellPrice =
    getNumberLikeString(product.sell_price) || getNumberLikeString(product.sale_price) || price;
  const shopSku =
    getStringValue(product.shop_sku) ||
    getStringValue(product.sku) ||
    `marketing-brand-fallback-${getStringValue(product.id) || 'item'}`;
  const name = getMarketingFlashSaleProductName(product) || 'Gallant';
  const stock = getFiniteNumber(product.stock);

  return {
    ...product,
    id: product.id ?? shopSku,
    name,
    short_name: getStringValue(product.short_name) || name,
    shop_sku: shopSku,
    business_sku: getStringValue(product.business_sku) || shopSku,
    image: productImage,
    price_info:
      Array.isArray(product.price_info) && product.price_info.length
        ? product.price_info
        : [
            {
              currency: 'USD',
              price,
              sell_price: sellPrice,
            },
          ],
    is_glass: Number(product.is_glass) || 1,
    is_wish: Number(product.is_wish) || 2,
    wish_num: Number(product.wish_num) || 0,
    status: Number(product.status) || 1,
    stock: stock ?? 1,
  } as unknown as Product;
}

export function getMarketingFlashSaleProductName(product: MarketingFlashSaleProductItem) {
  return (
    product.short_name ||
    product.name ||
    getStringValue(product.product_name) ||
    product.shop_sku ||
    ''
  );
}

export function getMarketingFlashSaleProductHref(product: MarketingFlashSaleProductItem) {
  const configuredUrl =
    getStringValue(product.url) ||
    getStringValue(product.href) ||
    getStringValue(product.product_url) ||
    getStringValue(product.jump_url) ||
    getStringValue(product.seo_url);
  if (configuredUrl) {
    return configuredUrl;
  }

  const sku =
    getStringValue(product.shop_sku) ||
    getStringValue(product.business_sku) ||
    getStringValue(product.sku);

  return sku ? `/goods-detail/${sku}` : '';
}

export function getMarketingFlashSaleDisplayPrice(
  product: MarketingFlashSaleProductItem,
  currency: unknown,
  userInfo: unknown,
) {
  const storePrice = priceInfo(userInfo, product, currency) as {
    price?: string;
    sell_price?: string;
  };

  const directSellPrice =
    getNumberLikeString(storePrice?.sell_price) ||
    getNumberLikeString(storePrice?.price) ||
    getNumberLikeString(product.sell_price) ||
    getNumberLikeString(product.sale_price) ||
    getNumberLikeString(product.price);
  const directPrice =
    getNumberLikeString(storePrice?.price) ||
    getNumberLikeString(product.original_price) ||
    getNumberLikeString(product.market_price) ||
    directSellPrice;

  return {
    price: directPrice,
    sellPrice: directSellPrice,
  };
}

export function isMarketingFlashSaleSoldOutProduct(product: MarketingFlashSaleProductItem) {
  const status = getFiniteNumber(product.status);
  const stock = getFiniteNumber(product.stock);

  return (
    (status !== undefined && status !== 1) ||
    (stock !== undefined && stock <= 0) ||
    String(product.in_stock) === '2'
  );
}

export function isMarketingProductSoldOut(product: Record<string, unknown>) {
  const status = getFiniteNumber(product.status);
  const stock = getFiniteNumber(product.stock);

  if (status !== undefined && status !== 1) {
    return true;
  }

  if (stock !== undefined && stock <= 0) {
    return true;
  }

  return String(product.in_stock) === '2';
}

export function getMarketingRankingSoldText(
  product: MarketingProductItem,
  formatSoldText: (count: string) => string,
) {
  const configuredText =
    getStringValue(product.sold_text) ||
    getStringValue(product.sales_text) ||
    getStringValue(product.rank_text) ||
    getStringValue(product.hot_text);
  if (configuredText) {
    return configuredText;
  }

  const soldCount = Number(
    product.sold_count ?? product.sales_count ?? product.sale_count ?? product.sold_num,
  );
  if (Number.isFinite(soldCount) && soldCount > 0) {
    const count =
      soldCount >= 1000
        ? `${(soldCount / 1000).toFixed(soldCount >= 10000 ? 0 : 1)}K`
        : String(soldCount);
    return formatSoldText(count);
  }

  return formatSoldText('2.5K');
}

export type MarketingRankingProductCardView = Pick<
  MarketingRankingProductCardProps,
  'href' | 'image' | 'imageAlt' | 'name' | 'priceText' | 'originalPriceText'
>;

export function getMarketingRankingProductCardView(
  product: MarketingProductItem,
  currency: unknown,
  userInfo: unknown,
): MarketingRankingProductCardView {
  const image = getMarketingFlashSaleProductImage(product);
  const name = getMarketingFlashSaleProductName(product);
  const href = getMarketingFlashSaleProductHref(product);
  const { price, sellPrice } = getMarketingFlashSaleDisplayPrice(product, currency, userInfo);
  const priceText = sellPrice ? formatPriceNumberWithSymbol(sellPrice, currency) : '$79.99';
  const originalPriceText = price ? formatPriceNumberWithSymbol(price, currency) : '';

  return {
    href: href || undefined,
    image: image || undefined,
    imageAlt: name,
    name,
    priceText,
    originalPriceText:
      originalPriceText && originalPriceText !== priceText ? originalPriceText : undefined,
  };
}
